import * as React from "react";

const desktop = "(min-width: 1024px)";

/**
 * Reads a CSS media query, SSR-safely.
 *
 * `useSyncExternalStore` is React's official pattern for an external store
 * with a server snapshot. The three callbacks (not nested hooks — plain
 * functions passed to React):
 *
 * - subscribe: register a `change` listener on the MediaQueryList and
 *   return cleanup. React uses it to know when to re-render.
 * - getSnapshot: read the client value during render.
 * - getServerSnapshot: return the value React should use during SSR AND
 *   during the initial client hydration pass.
 *
 * Returning `false` from the server snapshot makes server and initial
 * client render agree exactly (no hydration mismatch — LADDER-WEBSITE-894).
 * After hydration React switches to the client snapshot, so the real
 * matchMedia result lands on the second render.
 *
 * Trade-off: components that key off the breakpoint render once with
 * `false` before flipping to the real value. That's the same flash the
 * old useState+useEffect version had — but it's now without the hydration
 * error and with about a third less code. Avoiding the flash entirely
 * requires a cookie hint or UA sniff and the user impact today is zero
 * (React 19 absorbed the mismatch silently).
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = React.useCallback(
    (notify: () => void) => {
      const mq = window.matchMedia(query);
      mq.addEventListener("change", notify);
      return () => mq.removeEventListener("change", notify);
    },
    [query]
  );

  const getSnapshot = React.useCallback(
    () => window.matchMedia(query).matches,
    [query]
  );

  const getServerSnapshot = React.useCallback(() => false, []);

  return React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export const useIsMobile = () => {
  const value = useMediaQuery(desktop);
  return !value;
};

export const useIsDesktop = () => {
  const value = useMediaQuery(desktop);
  return value;
};
